import { useEffect, useMemo, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { where } from 'firebase/firestore'
import { app } from '../lib/firebase'
import { useCollection } from '../lib/firestore'
import { useAuth } from '../lib/auth'
import { formatDayLong, formatTime } from '../lib/format'
import { deleteItineraryItem } from '../lib/mutations'
import { cacheShowCoordinates, geocodeVenue } from '../lib/geocode'
import type { Flight, ItineraryItem, Show, Tour, WithId } from '../types/models'
import RouteMap, { type MapLeg, type MapPoint } from './RouteMap'
import Icon from './Icon'
import ItineraryDialog from '../dialogs/ItineraryDialog'
import FlightDialog from '../dialogs/FlightDialog'

/* ============================================================================
   Route.

   Shows what the user has actually made. Nothing here is invented.

   The first version of this drew a card for every gap between two shows,
   whether or not anyone had decided anything about it — a page of suggestions
   dressed up as a plan. That is noise: a tour manager knows there is a gap
   between Berkeley and Portland. What they want to see is what they've already
   arranged, and where it is on a map.

   So a leg exists here only because a person created one:

     · a Flight record
     · an itinerary item of type travel, flight or arrival

   Drives follow the real road geometry from the router. Flights bow away from
   the straight line, because a straight line on a map with roads on it reads
   as a road.
   ============================================================================ */

const routeBetween = httpsCallable<
  { from: { lat: number; lon: number }; to: { lat: number; lon: number } },
  { found: boolean; km: number | null; totalHours?: number; line?: number[][] }
>(getFunctions(app), 'routeBetween')

interface Made {
  id: string
  mode: 'drive' | 'fly'
  title: string
  detail: string
  whenMs: number
  /** What opens when the leg is clicked. */
  kind: 'itinerary' | 'flight'
  item?: WithId<ItineraryItem>
  flight?: WithId<Flight>
  /** Resolved from the shows either side, when they can be worked out. */
  from?: MapPoint
  to?: MapPoint
}

export default function RoutePlanner({ tour }: { tour: WithId<Tour> }) {
  const { uid } = useAuth()
  const scope = [where('tourId', '==', tour.id)]
  const shows = useCollection<Show>('shows', scope)
  const flights = useCollection<Flight>('flights', scope)
  const itinerary = useCollection<ItineraryItem>('itineraryItems', scope)

  const [selected, setSelected] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<WithId<ItineraryItem> | null>(null)
  const [editingFlight, setEditingFlight] = useState<WithId<Flight> | null>(null)
  const [lines, setLines] = useState<Record<string, number[][]>>({})

  const located = useMemo(
    () =>
      [...shows.data]
        .filter((show) => typeof show.venueLat === 'number' && typeof show.venueLon === 'number')
        .sort((a, b) => a.date.toMillis() - b.date.toMillis()),
    [shows.data],
  )

  /**
   * Fills in coordinates for shows that have never been opened.
   *
   * They're cached onto the show the first time anybody views it, which means
   * a tour nobody has clicked through has an empty map — the one case where a
   * map is most useful. Geocoded here, once, in date order, and written back
   * so every client benefits.
   */
  useEffect(() => {
    const missing = shows.data.filter(
      (show) => typeof show.venueLat !== 'number' && (show.venueAddress || show.city),
    )
    if (missing.length === 0) return

    let alive = true
    void (async () => {
      // One at a time: Nominatim asks for low volume, and a burst on behalf of
      // a twenty-date tour is exactly what its policy is about.
      for (const show of missing.slice(0, 25)) {
        if (!alive) return
        const query = [show.venueName, show.venueAddress, show.city, show.country]
          .filter(Boolean)
          .join(', ')
        const found = await geocodeVenue(query).catch(() => null)
        if (found) await cacheShowCoordinates(show.id, found).catch(() => {})
      }
    })()

    return () => {
      alive = false
    }
  }, [shows.data])

  const points: MapPoint[] = located.map((show) => ({
    id: show.id,
    lat: show.venueLat!,
    lon: show.venueLon!,
    label: show.city || show.venueName,
  }))

  /**
   * Every leg somebody made, in time order.
   *
   * Each one is anchored to the pair of shows it sits between, which is what
   * gives it two ends to draw. A leg that can't be anchored — travel before
   * the first show, or on a tour whose venues have no coordinates — still
   * lists, it just doesn't draw.
   */
  const made: Made[] = useMemo(() => {
    const between = (whenMs: number) => {
      const before = [...located].reverse().find((show) => show.date.toMillis() <= whenMs)
      const after = located.find((show) => show.date.toMillis() > whenMs)
      const asPoint = (show?: WithId<Show>): MapPoint | undefined =>
        show ? { id: show.id, lat: show.venueLat!, lon: show.venueLon!, label: show.city } : undefined
      return { from: asPoint(before), to: asPoint(after) }
    }

    const fromFlights: Made[] = flights.data.map((flight) => {
      const whenMs = flight.departureTimeUTC.toMillis()
      return {
        id: `flight-${flight.id}`,
        mode: 'fly',
        title: `${flight.origin} to ${flight.destination}`,
        detail: [
          [flight.airline, flight.flightNumber].filter(Boolean).join(' '),
          formatDayLong(flight.departureTimeUTC),
          formatTime(flight.departureTimeUTC),
        ]
          .filter(Boolean)
          .join(' · '),
        whenMs,
        kind: 'flight',
        flight,
        ...between(whenMs),
      }
    })

    const fromItinerary: Made[] = itinerary.data
      .filter((item) => ['travel', 'flight', 'arrival'].includes(item.type))
      // Arrivals are the other half of a leg somebody already made, not a leg
      // of their own, so they'd double every drive.
      .filter((item) => item.type !== 'arrival')
      // Saving a flight also writes an itinerary item for it, so listing both
      // showed every flight twice. `airlineCode` is only set by newer writes,
      // so the reliable test is the clock: a flight-type item within an hour
      // of a real Flight is that Flight's shadow. The Flight record wins,
      // because it holds the passengers and the booking reference.
      .filter(
        (item) =>
          item.type !== 'flight' ||
          !flights.data.some(
            (flight) =>
              Math.abs(flight.departureTimeUTC.toMillis() - item.timeUTC.toMillis()) < 3_600_000,
          ),
      )
      .map((item) => {
        const whenMs = item.timeUTC.toMillis()
        return {
          id: `item-${item.id}`,
          mode: item.type === 'flight' ? 'fly' : 'drive',
          title: item.title,
          detail: [
            item.subtitle,
            formatDayLong(item.timeUTC, item.timezone),
            item.needsTime ? 'Time not set' : formatTime(item.timeUTC, item.timezone),
          ]
            .filter(Boolean)
            .join(' · '),
          whenMs,
          kind: 'itinerary' as const,
          item,
          ...between(whenMs),
        }
      })

    return [...fromFlights, ...fromItinerary].sort((a, b) => a.whenMs - b.whenMs)
  }, [flights.data, itinerary.data, located])

  // Real road shapes for the drives, once, per pair of ends.
  useEffect(() => {
    made
      .filter((leg) => leg.mode === 'drive' && leg.from && leg.to && !lines[leg.id])
      .forEach((leg) => {
        void routeBetween({
          from: { lat: leg.from!.lat, lon: leg.from!.lon },
          to: { lat: leg.to!.lat, lon: leg.to!.lon },
        })
          .then((response) => {
            const line = response.data.line
            if (line?.length) setLines((current) => ({ ...current, [leg.id]: line }))
          })
          .catch(() => {
            /* a leg without a drawn road still lists */
          })
      })
  }, [made, lines])

  const mapLegs: MapLeg[] = made
    .filter((leg) => leg.from && leg.to)
    .map((leg) => ({
      id: leg.id,
      from: leg.from!,
      to: leg.to!,
      mode: leg.mode,
      label: leg.title,
      line: lines[leg.id],
    }))

  const chosen = made.find((leg) => leg.id === selected) ?? null

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="spread">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Route</h2>
        <span className="t-caption">
          {made.length === 0
            ? 'Nothing planned yet'
            : `${made.length} leg${made.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <RouteMap
        points={points}
        legs={mapLegs}
        selectedLegId={selected}
        onSelectLeg={(leg) => setSelected(leg.id === selected ? null : leg.id)}
      />

      {made.length === 0 ? (
        <p className="panel panel-pad t-caption">
          Add a flight, or build a route from the Add button, and it appears here and on the map.
        </p>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {made.map((leg) => {
            const on = leg.id === selected
            return (
              <button
                key={leg.id}
                className="row panel"
                onClick={() => setSelected(on ? null : leg.id)}
                style={{
                  gap: 12,
                  padding: '11px 13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                  background: on ? 'var(--accent-wash)' : 'var(--panel)',
                }}
              >
                <Icon
                  name={leg.mode === 'fly' ? 'plane' : 'truck'}
                  size={16}
                  style={{ color: 'var(--text-tertiary)' }}
                />
                <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ fontWeight: 600 }}>
                    {leg.title}
                  </span>
                  <span className="t-caption truncate">{leg.detail}</span>
                </span>
                <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
              </button>
            )
          })}
        </div>
      )}

      {/* Selecting a leg on the map or in the list opens the same card. */}
      {chosen && (
        <div className="panel panel-pad stack" style={{ gap: 12 }}>
          <div className="spread" style={{ alignItems: 'flex-start', gap: 12 }}>
            <div className="stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="t-eyebrow">{chosen.mode === 'fly' ? 'Flight' : 'Drive'}</span>
              <span style={{ fontWeight: 600 }}>{chosen.title}</span>
              <span className="t-caption">{chosen.detail}</span>
            </div>
            <button className="btn btn-quiet" onClick={() => setSelected(null)}>
              <Icon name="close" size={15} />
            </button>
          </div>

          {chosen.from && chosen.to && (
            <span className="t-caption">
              {chosen.from.label} to {chosen.to.label}
            </span>
          )}

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => {
                if (chosen.kind === 'flight' && chosen.flight) setEditingFlight(chosen.flight)
                if (chosen.kind === 'itinerary' && chosen.item) setEditingItem(chosen.item)
              }}
            >
              <Icon name="edit" size={15} /> Edit
            </button>
            {chosen.kind === 'itinerary' && chosen.item && (
              <button
                className="btn btn-quiet"
                style={{ color: 'var(--destructive)' }}
                onClick={async () => {
                  await deleteItineraryItem(chosen.item!.id)
                  setSelected(null)
                }}
              >
                <Icon name="trash" size={15} /> Remove
              </button>
            )}
          </div>
        </div>
      )}

      {editingItem && uid && (
        <ItineraryDialog
          open
          tourId={tour.id}
          userId={uid}
          item={editingItem}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingFlight && uid && (
        <FlightDialog
          open
          tourId={tour.id}
          ownerId={uid}
          flight={editingFlight}
          onClose={() => setEditingFlight(null)}
        />
      )}
    </div>
  )
}
